import React from "react";
import { cn } from "~/lib/utils";

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "elevated" | "outlined" | "filled" | "interactive";
  size?: "sm" | "md" | "lg";
  hoverable?: boolean;
  children: React.ReactNode;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant = "default",
      size = "md",
      hoverable = false,
      children,
      ...props
    },
    ref
  ) => {
    const baseClasses = "bg-white dark:bg-neutral-800 transition-base";

    const variantClasses = {
      default: "card-shadow border border-neutral-200 dark:border-neutral-700",
      elevated: "shadow-lg border border-neutral-100 dark:border-neutral-700",
      outlined: "border-2 border-neutral-300 dark:border-neutral-600",
      filled:
        "bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700",
      interactive:
        "card-shadow border border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 cursor-pointer card-hover",
    };

    const sizeClasses = {
      sm: "card-padding-sm card-rounded",
      md: "card-padding-md card-rounded",
      lg: "card-padding-lg rounded-xl",
    };

    return (
      <div
        className={cn(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          hoverable && "card-hover cursor-pointer",
          className
        )}
        ref={ref}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = "Card";

// Card sub-components with design system styling
const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    title?: string;
    subtitle?: string;
    action?: React.ReactNode;
  }
>(({ className, title, subtitle, action, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-start justify-between mb-4 pb-4 border-b border-neutral-200 dark:border-neutral-700",
      className
    )}
    {...props}
  >
    <div className="flex-1">
      {title && (
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
          {title}
        </h3>
      )}
      {subtitle && (
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {subtitle}
        </p>
      )}
      {children}
    </div>
    {action && <div className="ml-4 flex-shrink-0">{action}</div>}
  </div>
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight text-neutral-900 dark:text-neutral-100",
      className
    )}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-neutral-500 dark:text-neutral-400", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-neutral-700 dark:text-neutral-300", className)}
    {...props}
  />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    justify?: "start" | "center" | "end" | "between";
  }
>(({ className, justify = "end", ...props }, ref) => {
  const justifyStyles = {
    start: "justify-start",
    center: "justify-center",
    end: "justify-end",
    between: "justify-between",
  };

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700",
        justifyStyles[justify],
        className
      )}
      {...props}
    />
  );
});
CardFooter.displayName = "CardFooter";

// New Card Image component
const CardImage = React.forwardRef<
  HTMLImageElement,
  React.ImgHTMLAttributes<HTMLImageElement> & {
    aspectRatio?: "square" | "video" | "wide" | "tall";
    objectFit?: "cover" | "contain" | "fill";
  }
>(
  (
    {
      className,
      aspectRatio = "video",
      objectFit = "cover",
      alt = "",
      ...props
    },
    ref
  ) => {
    const aspectRatioStyles = {
      square: "aspect-square",
      video: "aspect-video",
      wide: "aspect-[21/9]",
      tall: "aspect-[3/4]",
    };

    const objectFitStyles = {
      cover: "object-cover",
      contain: "object-contain",
      fill: "object-fill",
    };

    return (
      <div
        className={cn(
          "overflow-hidden rounded-t-lg",
          aspectRatioStyles[aspectRatio]
        )}
      >
        <img
          ref={ref}
          className={cn(
            "w-full h-full transition-transform hover:scale-105",
            objectFitStyles[objectFit],
            className
          )}
          alt={alt}
          {...props}
        />
      </div>
    );
  }
);
CardImage.displayName = "CardImage";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  CardImage,
};
